import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getAuthClient } from '../../utils/auth';

import { getNormalizedApiDataByUuid } from '../../utils/ApiHelpers';
import { useConfigStore, useAppStore, useAuthStore } from '../../stores';
import { createSalespersonName } from '../../utils/DrupalEntityTypes/User/UserHelpers';
import { userNormalizeData } from '../../utils/DrupalEntityTypes/User/UserNormalizeData';

import YourTourNavItem from './YourTourNavItem';
import Personalization from '../Personalization/Personalization';
import SlidingPanel from '../SlidingPanel/SlidingPanel';

import { ReactComponent as AmenitiesNavIcon } from '../../SVGAssets/nav-amenities.svg';
import { ReactComponent as GalleryNavIcon } from '../../SVGAssets/nav-gallery.svg';
import { ReactComponent as MapNavIcon } from '../../SVGAssets/nav-map.svg';
import { ReactComponent as FloorplanNavIcon } from '../../SVGAssets/nav-floorplans.svg';

import './YourTourNav.scss';

const auth = getAuthClient();

const YourTourNav = () => {
  const [panelIsOpen, setPanelIsOpen] = useState(true);
  const [showNavBar, setShowNavBar] = useState(false);
  const [salespersonCrmId, setSalespersonCrmId] = useState(null);

  // Zustand stores
  const config = useConfigStore((state) => state.config);
  const settings = useAppStore((state) => state.settings);
  const appStatus = useAppStore((state) => state.appStatus);
  const isLoggedIn = useAuthStore((state) => state.isLoggedIn);
  const salespersonId = useAuthStore((state) => state.salespersonId);
  const salespersonName = useAuthStore((state) => state.salespersonName);
  const setSalespersonName = useAuthStore((state) => state.setSalespersonName);

  const { communityLogo, hasMediaGallery } = config || {};
  const { hasChrome, platform } = settings || {};

  // Capture search params from current url location.
  const { search } = useLocation();

  const handleToggle = () => {
    setPanelIsOpen((prev) => !prev);
  };

  // Get and set user's sales person info.
  useEffect(() => {
    const fetchData = async () => {
      // Superadmin comes over as true instead of 1, so we need to convert it.
      const request = {
        entityType: 'user',
        contentType: 'user',
        uuid: salespersonId,
      };
      const data = await getNormalizedApiDataByUuid(request, userNormalizeData);
      if (data) {
        const rawSalesPersonName = createSalespersonName(data);
        setSalespersonName(rawSalesPersonName);
        const rawSalespersonCrmId = data.fieldCrmSalesCounselorId;
        setSalespersonCrmId(rawSalespersonCrmId);
      }
    };
    if (salespersonId) {
      fetchData();
    }
  }, [salespersonId]);

  // Should we render the nav?
  useEffect(() => {
    if (hasChrome) {
      setShowNavBar(true);
    } else {
      setShowNavBar(false);
    }
  }, [appStatus, hasChrome]);

  return showNavBar && (
    <SlidingPanel
      side="left"
      isOpen={panelIsOpen}
      onToggle={handleToggle}
    >
      <div className="yourTourNav">
        <Link
          className="logo"
          style={{ backgroundImage: `url(${communityLogo})` }}
          to={`/${search}`}
        />

        <div className="main-nav">
          <YourTourNavItem
            destination="/map"
            label="Interactive Map"
            Icon={MapNavIcon}
          />
          <YourTourNavItem
            destination="/amenities"
            label="Amenities"
            Icon={AmenitiesNavIcon}
          />
          <YourTourNavItem
            destination="/floor-plans"
            label="Floor Plans"
            Icon={FloorplanNavIcon}
          />
          {hasMediaGallery && (
            <YourTourNavItem
              destination="/media"
              label="Media Gallery"
              Icon={GalleryNavIcon}
            />
          )}
        </div>

        <Personalization salespersonCrmId={salespersonCrmId} />

        {platform !== 'kiosk' && isLoggedIn
          && (
            <div className="sidebar__message">
              You are logged in as
              {' '}
              {salespersonName}
              .
              {' '}
              <button
                type="button"
                className="sidebar__messageCta"
                onClick={() => {
                  auth.logout();
                }}
              >
                Log out.
              </button>
            </div>
          )}

        {platform !== 'kiosk' && (
          <div className="sidebar__message">
            Need help?
            {' '}
            <a
              href="https://yourtour.groovehq.com/help"
              target="_blank"
              rel="noopener noreferrer"
              className="sidebar__messageCta"
            >
              View our documentation.
            </a>
          </div>
        )}
      </div>
    </SlidingPanel>
  );
};

export default YourTourNav;
